'use client';

import { useState } from 'react';
import type { ChangeEvent } from 'react';
import { normalizeNumber, sanitizeDecimalInput } from './decimalInput';
import { useTouchedFields } from './useTouchedFields';
import { mapFieldValues } from './types';
import type { CalculatorField, CalculatorFieldValues } from './types';

export const useNumberFields = (
  initialValues: CalculatorFieldValues,
  /** Decimals kept on blur and Calculate; the default 0 settles a count such as a loan term to whole units. */
  maximumFractionDigits = 0,
) => {
  const [values, setValues] = useState<CalculatorFieldValues>(initialValues);
  const { markTouched, isTouched, resetTouched } = useTouchedFields();

  const normalizeValue = (raw: string) => normalizeNumber(raw, maximumFractionDigits);

  const updateValue = (label: string, transform: (raw: string) => string) =>
    setValues(previous => ({ ...previous, [label]: transform(previous[label]) }));

  const handleChange = (label: string, value: string) => {
    markTouched(label);
    updateValue(label, () => sanitizeDecimalInput(value));
  };

  const handleBlur = (label: string) => {
    updateValue(label, normalizeValue);
  };

  const normalize = (): CalculatorFieldValues => mapFieldValues(values, normalizeValue);

  const fieldProps = (field: CalculatorField, error?: string) => ({
    label: field.label,
    hint: field.hint,
    placeholder: field.placeholder,
    inputMode: 'decimal' as const,
    value: values[field.label],
    error,
    invalid: Boolean(error),
    onChange: (event: ChangeEvent<HTMLInputElement>) => handleChange(field.label, event.target.value),
    onBlur: () => handleBlur(field.label),
  });

  return {
    values,
    normalize,
    commit: () => setValues(normalize()),
    reset: () => {
      resetTouched();
      setValues(initialValues);
    },
    isTouched,
    fieldProps,
  };
};

/** Lets a form component take the factory without restating `CalculatorInput`'s props. */
export type NumberFieldProps = ReturnType<typeof useNumberFields>['fieldProps'];
